/**
 * US federal holidays + a few widely-celebrated cultural observances.
 *
 * All dates are computed in the **local calendar** — no timezone math —
 * so a holiday rendered on a calendar cell always lands on the same
 * civil date the user reads on their wall calendar.
 *
 * Federal holidays use the "observed" date when the actual date falls
 * on a weekend, matching the OPM closure schedule (Sat → Fri, Sun → Mon).
 * Both the actual date and the observed date are emitted so the cell
 * still shows e.g. "🇺🇸 Juneteenth" on June 19 even when offices are
 * closed on the 20th.
 */

// Fixed-day federal holidays. Format: [month (1-12), day, name]
const FIXED = [
  [1, 1, "New Year's Day"],
  [6, 19, "Juneteenth"],
  [7, 4, "Independence Day"],
  [11, 11, "Veterans Day"],
  [12, 25, "Christmas Day"],
];

/** N-th weekday of a month (e.g. 3rd Monday in January = MLK Day). */
function nthWeekday(year, monthIdx, weekday, n) {
  // First instance of `weekday` in the month
  const first = new Date(year, monthIdx, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  return new Date(year, monthIdx, 1 + offset + (n - 1) * 7);
}

/** Last weekday of a month (e.g. last Monday in May = Memorial Day). */
function lastWeekday(year, monthIdx, weekday) {
  const last = new Date(year, monthIdx + 1, 0); // last day of the month
  const offset = (last.getDay() - weekday + 7) % 7;
  return new Date(year, monthIdx, last.getDate() - offset);
}

/** ISO YYYY-MM-DD in local time (avoids toISOString UTC shifting). */
function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Federal "observed" rule: if a fixed holiday lands on Saturday, observe
 * the preceding Friday; if Sunday, observe the following Monday.
 */
function observedFor(d) {
  const dow = d.getDay();
  if (dow === 6) return new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
  if (dow === 0) return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  return null;
}

/**
 * Returns an object map of `YYYY-MM-DD → [{ name, federal, observed }]`
 * for the given year (covering everything visible on a year-long calendar).
 */
export function usHolidaysForYear(year) {
  const out = {};
  const push = (d, name, federal = true, observed = false) => {
    const key = isoLocal(d);
    (out[key] ||= []).push({ name, federal, observed });
  };

  // Fixed-date federal holidays + their observed-date shadow
  FIXED.forEach(([m, day, name]) => {
    const actual = new Date(year, m - 1, day);
    push(actual, name, true, false);
    const obs = observedFor(actual);
    if (obs) push(obs, `${name} (Observed)`, true, true);
  });

  // Moveable federal holidays
  push(nthWeekday(year, 0, 1, 3),  "Martin Luther King Jr. Day");
  push(nthWeekday(year, 1, 1, 3),  "Presidents' Day");
  push(lastWeekday(year, 4, 1),    "Memorial Day");
  push(nthWeekday(year, 8, 1, 1),  "Labor Day");
  push(nthWeekday(year, 9, 1, 2),  "Columbus Day");
  push(nthWeekday(year, 10, 4, 4), "Thanksgiving");
  // Black Friday — day after Thanksgiving (commonly observed)
  const thx = nthWeekday(year, 10, 4, 4);
  push(new Date(year, 10, thx.getDate() + 1), "Day after Thanksgiving", false);

  // Widely-tracked non-federal observances
  push(new Date(year, 1, 14),  "Valentine's Day", false);
  push(new Date(year, 2, 17),  "St. Patrick's Day", false);
  push(new Date(year, 9, 31),  "Halloween", false);
  push(new Date(year, 11, 31), "New Year's Eve", false);
  // Mother's Day — 2nd Sunday in May
  push(nthWeekday(year, 4, 0, 2), "Mother's Day", false);
  // Father's Day — 3rd Sunday in June
  push(nthWeekday(year, 5, 0, 3), "Father's Day", false);

  return out;
}

/**
 * Build a lookup map covering the prev/current/next year so a calendar
 * cell crossing year boundaries always finds the right entry.
 */
export function usHolidaysAround(year) {
  return {
    ...usHolidaysForYear(year - 1),
    ...usHolidaysForYear(year),
    ...usHolidaysForYear(year + 1),
  };
}

/** Convenience: get holidays falling on a specific JS Date (local). */
export function holidaysOn(date, lookup) {
  return lookup[isoLocal(date)] || [];
}
